import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { fetchCourse, fetchCourses, saveProgress } from "./coursesAPI";
import { getOrganisationId } from "../organisations/organisationsSlice";
import { catchAllErrors } from "../../hoc/catchAllErrors";

export const fetchCoursesAsync = createAsyncThunk("courses/fetchCourses", async (userId) => {
    try {
        if (!userId) return [];
        const response = await catchAllErrors(fetchCourses)(userId);
        return response;
    } catch (error) {
        toast("Failed to fetch courses", { type: "error" });
        throw error;
    }
});

export const fetchCourseItemAsync = createAsyncThunk("courses/fetchCourse", async (id) => {
    try {
        if (!id) return null;
        const response = await catchAllErrors(fetchCourse)(id);
        return response;
    } catch (error) {
        toast("Failed to fetch course", { type: "error" });
        console.error(error);
        throw error;
    }
});

export const saveProgressAsync = createAsyncThunk("courses/saveProgress", async ({ id, data }) => {
    try {
        console.log('>>> SAVING PROGRESS', id, data);
        const updatedResponse = await catchAllErrors(saveProgress)(id, data);
        const updated = await updatedResponse.json();
        const response = await catchAllErrors(fetchCourse)(id);
        return {
            ...response,
            attributes: {
                ...response.attributes,
                ...updated.data.attributes,
            },
        };
    } catch (error) {
        toast("Failed to save progress", { type: "error" });
        console.error(error);
        throw error;
    }
});

export const saveProgressDelay = createAsyncThunk("courses/saveProgress", async ({ id, data }) => {
    try {
        const updatedResponse = await catchAllErrors(saveProgress)(id, data);
        const updated = await updatedResponse.json();
        const response = await catchAllErrors(fetchCourse)(id);
        return {
            ...response,
            attributes: {
                ...response.attributes,
                ...updated.data.attributes,
            },
        };
    } catch (error) {
        toast("Failed to save progress", { type: "error" });
        console.error(error);
        throw error;
    }
});

export const coursesSlice = createSlice({
    name: "courses",
    initialState: {
        courses: null,
        course: null,
        meta: {},
        status: "idle",
        error: null,
    },
    reducers: {
        resetStatus: (state) => {
            state.status = "idle";
            state.error = null;
        },
        setCourses: (state, action) => {
            state.courses = action.payload;
        },
        setCourse: (state, action) => {
            state.course = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCoursesAsync.pending, (state) => {
                state.courses = [];
                state.status = "pending";
            })
            .addCase(fetchCoursesAsync.fulfilled, (state, action) => {
                state.courses = action.payload;
                state.status = "fulfilled";
            })
            .addCase(fetchCoursesAsync.rejected, (state, action) => {
                state.courses = [];
                state.status = "rejected";
                state.error = action.error.message;
            })
            .addCase(fetchCourseItemAsync.pending, (state) => {
                state.course = null;
                state.status = "pending";
            })
            .addCase(fetchCourseItemAsync.fulfilled, (state, action) => {
                state.course = action.payload;
                state.status = "fulfilled";
            })
            .addCase(fetchCourseItemAsync.rejected, (state, action) => {
                state.course = null;
                state.status = "rejected";
                state.error = action.error.message;
            })
            .addCase(saveProgressAsync.pending, (state) => {
                state.status = "pending";
            })
            .addCase(saveProgressAsync.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.course = action.payload;
                state.courses = state.courses?.map((course) => {
                    if (course.id === action.payload.id) {
                        return action.payload;
                    }
                    return course;
                });
            })
            .addCase(saveProgressAsync.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.error.message;
            });
    },
});

export const { setCourses, setCourse, resetStatus } = coursesSlice.actions;

export const selectCourses = (state) => state.courses.courses;

export const selectCourse = (state) => state.courses.course;

export default coursesSlice.reducer;
